import { isPlatformBrowser } from '@angular/common';
import { Component, DestroyRef, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Observable, catchError, map, of, switchMap } from 'rxjs';

import {
  FrontendCollectionStore,
  FrontendCoupon,
  FrontendCouponBadgeDetail,
  FrontendCouponBadgeFaq,
} from '../../../core/api/api.models';
import { AssetUrlPipe } from '../../../core/api/asset-url.pipe';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { TrustedCmsHtmlPipe } from '../../../core/api/trusted-cms-html.pipe';
import { SectionHeading } from '../../../shared/section-heading/section-heading';
import { SeoService } from '../../../core/seo/seo.service';
import { SsrResponseService } from '../../../core/seo/ssr-response.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { faqPageSchema, webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { StoreLogo } from '../../../shared/store-logo/store-logo';
import { COUPON_COLLECTION_BY_SLUG, CouponCollectionDef } from '../coupon-collections';

/**
 * A resolved collection — either one of the four static system collections or a dynamic custom badge.
 * `filter` is the query it passes to `/coupons` (system: `{ isVerified: 'true' }`; custom: `{ badge: slug }`).
 * Content fields (metaTitle … faqs) come from the badge's admin-authored SEO & Content.
 */
interface ResolvedCollection {
  slug: string;
  eyebrow: string;
  title: string;
  heroHeading: string;
  pillLabel: string;
  description: string;
  badge: string;
  filter: Record<string, string>;
  metaTitle: string;
  metaDescription: string;
  longContent: string;
  faqs: FrontendCouponBadgeFaq[];
}

type CollectionDef = ResolvedCollection;

/** Coupon card view model for the collection grid. */
interface CollectionCouponCard {
  id: string;
  title: string;
  description: string;
  discountLabel: string;
  isDeal: boolean;
  code: string;
  /**
   * Last three characters of the code, shown next to "Reveal Code" before it is revealed — the same
   * teaser the home page's trending card uses. Empty for a deal.
   */
  codeSuffix: string;
  actionLabel: string;
  expiryText: string;
  /** Short expiry for the card footer ("Soon" when there is no date), as on the home page. */
  expiresLabel: string;
  isVerified: boolean;
  isExclusive: boolean;
  freeShipping: boolean;
  studentDiscount: boolean;
  badges: { label: string; color: string; iconUrl: string }[];
  storeName: string;
  storeSlug: string;
  storeLogoUrl: string;
  storeInitials: string;
}

/** Store card for the "Stores with <badge> coupons" grid. */
interface CollectionStoreCard {
  name: string;
  slug: string;
  logoUrl: string;
  initials: string;
  countLabel: string;
}

@Component({
  selector: 'app-coupon-collection-page',
  imports: [Breadcrumb, RouterLink, AssetUrlPipe, TrustedCmsHtmlPipe, SectionHeading, StoreLogo],
  templateUrl: './coupon-collection-page.html',
  styleUrl: './coupon-collection-page.scss',
})
export class CouponCollectionPage {
  private readonly api = inject(SavingArenaApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly seo = inject(SeoService);
  private readonly ssrResponse = inject(SsrResponseService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly PAGE_SIZE = 24;

  protected readonly collection = signal<CollectionDef | null>(null);

  /**
   * The page always showed "Home / Coupons / <collection>" but told Google "Home / <collection>" —
   * the /coupons hub was missing from the schema. One definition now feeds both.
   */
  protected readonly crumbs = computed(() => {
    const title = this.collection()?.title || '';
    return breadcrumbTrail(
      { label: 'Coupons', path: '/coupons' },
      ...(title ? [{ label: title }] : []),
    );
  });
  protected readonly coupons = signal<CollectionCouponCard[]>([]);
  protected readonly stores = signal<CollectionStoreCard[]>([]);
  protected readonly total = signal(0);
  protected readonly isLoading = signal(true);
  protected readonly notFound = signal(false);
  protected readonly loadingMore = signal(false);
  private readonly page = signal(1);
  private readonly loadedIds = new Set<string>();

  /** Reveal state for "Show Code" buttons, by coupon id. */
  protected readonly revealedCodes = signal<Record<string, boolean>>({});

  /** Index of the currently expanded FAQ (-1 = none). */
  protected readonly expandedFaq = signal(-1);

  protected readonly hasMore = computed(() => this.coupons().length < this.total());

  constructor() {
    this.route.paramMap
      .pipe(
        map((params) => params.get('collection') || ''),
        switchMap((slug) => {
          this.resetState();
          return this.resolveCollection(slug).pipe(
            switchMap((def) => {
              this.collection.set(def);
              if (!def) {
                return of(null);
              }
              return this.api
                .getCoupons({ ...def.filter, page: 1, limit: this.PAGE_SIZE })
                .pipe(catchError(() => of(null)));
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.isLoading.set(false);
        const def = this.collection();

        if (!def) {
          this.handleNotFound();
          return;
        }

        if (response?.success) {
          this.total.set(response.pagination?.total ?? 0);
          this.appendCoupons(response.data ?? []);
        }

        this.loadStores(def);
        this.applySeo(def);
      });
  }

  private resetState(): void {
    this.collection.set(null);
    this.coupons.set([]);
    this.stores.set([]);
    this.loadedIds.clear();
    this.page.set(1);
    this.total.set(0);
    this.revealedCodes.set({});
    this.expandedFaq.set(-1);
    this.isLoading.set(true);
    this.notFound.set(false);
  }

  /**
   * Fetches the badge (system or custom) by slug so its admin-authored SEO & content drive the page.
   * Falls back to the static system-collection copy when the badge fetch fails or is missing.
   */
  private resolveCollection(slug: string): Observable<ResolvedCollection | null> {
    const staticDef = COUPON_COLLECTION_BY_SLUG[slug];
    return this.api.getCouponBadge(slug).pipe(
      map((response) => {
        const badge = response?.success ? response.data : null;
        if (badge) {
          return this.fromBadge(badge, staticDef);
        }
        return staticDef ? this.fromStaticDef(staticDef) : null;
      }),
      catchError((error: unknown) => {
        this.ssrResponse.noteFetchFailure(error);
        return of(staticDef ? this.fromStaticDef(staticDef) : null);
      }),
    );
  }

  private fromStaticDef(def: CouponCollectionDef): ResolvedCollection {
    return {
      slug: def.slug,
      eyebrow: def.eyebrow,
      title: def.title,
      heroHeading: def.heroHeading,
      pillLabel: def.pillLabel,
      description: def.description,
      badge: def.badge,
      filter: { [def.filterParam]: 'true' },
      metaTitle: '',
      metaDescription: '',
      longContent: '',
      faqs: [],
    };
  }

  /** Admin content wins; the static system-collection copy (for the 4) or generated defaults fill gaps. */
  private fromBadge(badge: FrontendCouponBadgeDetail, staticDef?: CouponCollectionDef): ResolvedCollection {
    const name = badge.name?.trim() || 'Coupons';
    const label = badge.label?.trim() || name;
    return {
      slug: badge.slug,
      eyebrow: staticDef?.eyebrow || label,
      title: staticDef?.title || `${name} Coupons`,
      heroHeading: badge.h1?.trim() || staticDef?.heroHeading || `${name} coupon codes & deals.`,
      pillLabel: staticDef?.pillLabel || `${label.toLowerCase()} offers`,
      description:
        badge.shortDescription?.trim() ||
        staticDef?.description ||
        badge.description?.trim() ||
        `Hand-picked coupons and deals tagged ${name}.`,
      badge: label,
      filter:
        badge.filterParam === 'badge'
          ? { badge: badge.filterValue }
          : { [badge.filterParam]: badge.filterValue },
      metaTitle: badge.metaTitle?.trim() || '',
      metaDescription: badge.metaDescription?.trim() || '',
      longContent: badge.longContent?.trim() || '',
      faqs: Array.isArray(badge.faqs) ? badge.faqs.filter((faq) => faq.question || faq.answer) : [],
    };
  }

  protected toggleFaq(index: number): void {
    this.expandedFaq.update((current) => (current === index ? -1 : index));
  }

  /** Load the stores that have coupons in this collection for the "Stores with … coupons" grid. */
  private loadStores(def: CollectionDef): void {
    this.api
      .getCouponCollectionStores({ ...def.filter, limit: 24 })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (!response?.success) return;
        this.stores.set((response.data ?? []).map((store) => this.toStoreCard(store)));
      });
  }

  private toStoreCard(store: FrontendCollectionStore): CollectionStoreCard {
    const name = store.name?.trim() || 'Store';
    const count = Number(store.activeCouponsCount) || 0;
    return {
      name,
      slug: store.slug ? String(store.slug) : '',
      logoUrl: store.logoImagePath ? String(store.logoImagePath) : '',
      initials: this.initials(name),
      countLabel: `${count}+ Coupons & Deals`,
    };
  }

  private handleNotFound(): void {
    this.notFound.set(true);
    this.ssrResponse.setNotFound();
    this.seo.apply({
      title: 'Coupons not found | SavingArena',
      description: 'The coupon collection you are looking for is not available.',
      noindex: true,
    });
    this.seo.setJsonLd([]);
  }

  private applySeo(def: CollectionDef): void {
    const path = `/coupons/${this.route.snapshot.paramMap.get('collection')}`;
    const title = def.metaTitle || `${def.title} | SavingArena`;
    const description = def.metaDescription || def.description;

    this.seo.apply({ title, description, canonicalPath: path });

    const jsonLd = [
      webPageSchema({
        origin: this.seo.origin,
        url: this.seo.absolute(path),
        name: title,
        description,
      }),
      breadcrumbSchemaFor(this.crumbs(), (p) => this.seo.absolute(p)),
    ];
    const faqSchema = def.faqs.length ? faqPageSchema(def.faqs) : null;
    if (faqSchema) jsonLd.push(faqSchema);
    this.seo.setJsonLd(jsonLd);
  }

  protected loadMore(): void {
    const def = this.collection();
    if (!def || this.loadingMore() || !this.hasMore()) {
      return;
    }

    const next = this.page() + 1;
    this.loadingMore.set(true);

    this.api
      .getCoupons({ ...def.filter, page: next, limit: this.PAGE_SIZE })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.loadingMore.set(false);
        if (!response?.success) return;
        this.page.set(next);
        this.appendCoupons(response.data ?? []);
      });
  }

  /**
   * Hero store search. Looks up the query against the public search endpoint and jumps to the
   * best-matching store page; falls back to the full store list when nothing matches. Runs
   * browser-only — the input can't be submitted during the server render.
   */
  protected onSearch(event: Event, query: string): void {
    event.preventDefault();
    const term = query.trim();
    if (!term || !this.isBrowser) return;

    this.api
      .search(term)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        const store = response?.success ? response.data?.stores?.[0] : null;
        const slug = store?.slug ? String(store.slug) : '';
        this.router.navigate([slug ? `/${slug}` : '/storelist']);
      });
  }

  protected toggleCode(card: CollectionCouponCard): void {
    if (!card.code) return;
    const revealing = !this.isRevealed(card.id);
    this.revealedCodes.update((current) => ({ ...current, [card.id]: !current[card.id] }));

    // Count the reveal only — collapsing the code again is the same user backing out, not a second
    // action on the offer. Nothing here is awaited: the code must appear whether or not the beacon
    // lands, and a tracking failure should never surface to the shopper.
    if (revealing) this.recordHit(card.id);
  }

  /**
   * Fires the Hits beacon for one coupon. Browser-only: during SSR there is no click, and firing it
   * there would count every server render as a user action.
   */
  private recordHit(couponId: string): void {
    if (!this.isBrowser || !couponId) return;
    this.api
      .recordCouponHit(couponId)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected isRevealed(id: string): boolean {
    return this.revealedCodes()[id] === true;
  }

  private appendCoupons(raw: FrontendCoupon[]): void {
    const fresh = raw
      .filter((coupon) => {
        const id = String(coupon.id ?? '');
        if (!id || this.loadedIds.has(id)) return false;
        this.loadedIds.add(id);
        return true;
      })
      .map((coupon) => this.toCard(coupon));

    if (fresh.length) {
      this.coupons.update((cards) => [...cards, ...fresh]);
    }
  }

  private toCard(coupon: FrontendCoupon): CollectionCouponCard {
    const badges = (coupon.couponBadges ?? []).map((badge) => String(badge || '').trim());
    const store = coupon.affiliateStore || coupon.store;
    const storeName = store?.name?.trim() || 'SavingArena';
    const code = String(coupon.couponCode || '').trim();
    const isDeal = coupon.isDeal !== false && !code;
    const logoPath = store?.logoImagePath ? String(store.logoImagePath) : '';

    return {
      id: String(coupon.id ?? ''),
      title: coupon.name || 'Special Offer',
      description: String(coupon.detail || '').replace(/<[^>]*>/g, '').trim(),
      // First badge is the store's headline discount label (e.g. "70%").
      discountLabel: badges[0] || (isDeal ? 'Deal' : 'Code'),
      isDeal,
      code,
      codeSuffix: code ? code.slice(-3) : '',
      actionLabel: isDeal ? 'Get Deal' : 'Show Code',
      expiryText: String(coupon.endDateText || '').trim() || 'Limited Time Offer',
      expiresLabel: String(coupon.endDateText || '').trim() || 'Soon',
      isVerified: Boolean(coupon['isVerified']),
      isExclusive: Boolean(coupon.isExclusive),
      freeShipping: Boolean(coupon['freeShipping']),
      studentDiscount: Boolean(coupon['studentDiscount']),
      badges: (coupon.badges ?? []).map((badge) => ({
        label: String(badge.label || badge.name || '').trim(),
        color: String(badge.color || '#741FA2'),
        iconUrl: badge.iconImagePath ? String(badge.iconImagePath) : '',
      })),
      storeName,
      storeSlug: store?.slug ? String(store.slug) : '',
      storeLogoUrl: logoPath,
      storeInitials: this.initials(storeName),
    };
  }

  private initials(name: string): string {
    return (name || 'S')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() ?? '')
      .join('');
  }
}
